import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/lib/auth";
import HomePage from "@/pages/Home";
import HomeSwitchPage from "@/pages/HomeSwitch";
import AktionHomePage from "@/pages/AktionHome";
import { useAktionPage } from "@/lib/useAktionPage";
import AdminAktionsSeitePage from "@/pages/AdminAktionsSeite";
import SyltPage from "@/pages/Sylt";
import ErlebenPage from "@/pages/Erleben";
import { useErleben } from "@/lib/useErleben";
import { Navigate } from "react-router-dom";
import ImpressumPage from "@/pages/Impressum";
import DatenschutzPage from "@/pages/Datenschutz";
import BdvKontaktPage from "@/pages/BdvKontakt";
import NotFoundPage from "@/pages/NotFound";
import ProjectPage from "@/pages/Project";
import AdminLoginPage from "@/pages/AdminLogin";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboardPage from "@/pages/AdminDashboard";
import {
    AdminAnfragenListPage,
    AdminAnfragenDetailPage,
} from "@/pages/AdminAnfragen";
import AdminMediaPage from "@/pages/AdminMedien";
import AdminVorherNachherPage from "@/pages/AdminVorherNachher";
import AdminVorherNachher360Page from "@/pages/AdminVorherNachher360";
import AdminSkyboxPage from "@/pages/AdminSkybox";
import {
    AdminProjekteListPage,
    AdminProjektDetailPage,
} from "@/pages/AdminProjekte";
import AdminStartseitePage from "@/pages/AdminStartseite";
import AdminTrustBoxPage from "@/pages/AdminTrustBox";
import AdminViewerCtaPage from "@/pages/AdminViewerCta";
import AdminAktionPage from "@/pages/AdminAktion";
import AdminRechtstextePage from "@/pages/AdminRechtstexte";
import AdminSyltPage from "@/pages/AdminSylt";
import AdminQRKampagnePage from "@/pages/AdminQRKampagne";
import AdminAnalyticsPage from "@/pages/AdminAnalytics";
import AdminAnalyticsSessionsPage from "@/pages/AdminAnalyticsSessions";
import AdminTagesberichtPage from "@/pages/AdminTagesbericht";
import PostViewerCta from "@/components/PostViewerCta";
import AktionBanner from "@/components/AktionBanner";
import { AdminVertreterListPage, AdminVertreterDetailPage } from "@/pages/AdminVertreter";
import AdminVertreterRequestsPage from "@/pages/AdminVertreterRequests";
import VtCatch from "@/pages/VtCatch";
import ScrollToTop from "@/components/ScrollToTop";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import { useAnalytics } from "@/lib/useAnalytics";
import "@/App.css";

function AnalyticsTracker() {
    useAnalytics();
    return null;
}

/**
 * Route-Guard für `/erleben`. Liest den CMS-Master-Switch `erleben.enabled`
 * via `useErleben()`. Solange die CMS-Daten noch nicht geladen sind
 * (`ready=false`), wird die Seite normal gerendert — damit gibt es keinen
 * Flash-of-Redirect beim ersten Öffnen. Sobald der Fetch da ist und
 * `enabled=false` gemeldet wird, wird nach `/` umgeleitet.
 */
function ErlebenGate() {
    const { ready, enabled } = useErleben();
    if (ready && !enabled) return <Navigate to="/" replace />;
    return <ErlebenPage />;
}

/**
 * Route-Guard für `/aktion` (Ads-Landing-Page). Liest `aktion_page.enabled`
 * via `useAktionPage()`. Verhalten identisch zu `ErlebenGate` — kein Flash-
 * of-Redirect während des initialen Fetches, harter Redirect nach `/` sobald
 * bestätigt, dass die Landing deaktiviert ist.
 *
 * WICHTIG: Dieser Gate hängt am SEPARATEN Master-Switch der Landing, NICHT
 * am `aktion.enabled`-Feld (das steuert die Referenz-Sektion + Banner auf
 * der Startseite `/`).
 */
function AktionGate() {
    const { ready, enabled } = useAktionPage();
    if (ready && !enabled) return <Navigate to="/" replace />;
    return <AktionHomePage />;
}

function App() {
    // Globaler Orientation-Lock + Anchor-basierte Scroll-Preservation.
    //
    // Warum? Bei Portrait↔Landscape ändert sich die Höhe der 360°-Sektionen
    // dramatisch (z. B. 600 → 300 px). `window.scrollY` bleibt in Pixeln
    // konstant → derselbe Pixel-Wert zeigt nach dem Reflow eine ANDERE
    // Sektion (z. B. „Pakete" statt „360°-Transformationen"). Browser-Scroll-
    // Anchoring (siehe auch `overflow-anchor: none` in App.css) reicht dafür
    // nicht — wir speichern die sichtbare Sektion selbst und korrigieren
    // scrollY nach dem Layout-Settle.
    useEffect(() => {
        // Anchor: das Element mittig im Viewport (bevorzugt die nächste
        // Section/[id]-Grenze — stabile Referenz, kein flackernder Text).
        let anchorEl = null;
        let anchorTop = 0;

        const preserveScroll = () => {
            const cx = window.innerWidth / 2;
            const cy = window.innerHeight / 2;
            const el = document.elementFromPoint(cx, cy);
            if (!el) return;
            anchorEl =
                el.closest(
                    'section, [data-section], [id]',
                ) || el;
            anchorTop = anchorEl.getBoundingClientRect().top;
        };
        const restoreScroll = () => {
            if (!anchorEl) return;
            const el = anchorEl;
            // rAF + kleiner Delay: iOS-Statusbar-/Chrome-URL-Bar-Reflow
            // braucht ~150 ms bis er stabil ist. Erst DANN messen und
            // korrigieren, sonst rechnen wir mit Zwischen-Werten.
            requestAnimationFrame(() => {
                window.setTimeout(() => {
                    if (!document.body.contains(el)) {
                        anchorEl = null;
                        return;
                    }
                    const now = el.getBoundingClientRect().top;
                    const diff = now - anchorTop;
                    // Toleranz 50 px, sonst wackeln kleine Reflow-Deltas.
                    if (Math.abs(diff) > 50) {
                        window.scrollBy({ top: diff, behavior: 'instant' });
                    }
                    anchorEl = null;
                }, 150);
            });
        };

        const onOrient = () => {
            preserveScroll();
            window.__hf_orientation_locked__ = true;
            window.setTimeout(() => {
                window.__hf_orientation_locked__ = false;
            }, 1200);
            restoreScroll();
        };
        window.addEventListener("orientationchange", onOrient);

        // Zusätzlich: preserveScroll kontinuierlich beim Scroll cachen und
        // bei jedem resize (VOR jedem Reflow-Frame) refresh. So haben wir
        // beim orientationchange bereits eine gute Referenz — auch in
        // Browsern/Test-Umgebungen, die `resize` VOR `orientationchange`
        // feuern (oder gar kein orientationchange-Event schicken).
        let scrollPreserveScheduled = false;
        const schedulePreserveOnScroll = () => {
            if (scrollPreserveScheduled) return;
            scrollPreserveScheduled = true;
            requestAnimationFrame(() => {
                scrollPreserveScheduled = false;
                if (!window.__hf_orientation_locked__) preserveScroll();
            });
        };
        window.addEventListener("scroll", schedulePreserveOnScroll, { passive: true });

        // Desktop-Fallback: 3+ rapid resize-Events → auch Lock + Anchor.
        let lastResize = 0;
        let resizeSpree = 0;
        const onResize = () => {
            const now = performance.now();
            if (now - lastResize < 100) resizeSpree += 1;
            else resizeSpree = 0;
            lastResize = now;
            // JEDES Resize-Event, das nicht während Lock läuft, refresht
            // den Anchor. So haben wir garantiert einen aktuellen Wert,
            // egal ob die Reflow-Reihenfolge orientationchange→resize
            // oder resize→orientationchange ist.
            if (!window.__hf_orientation_locked__) preserveScroll();
            if (resizeSpree >= 3) {
                window.__hf_orientation_locked__ = true;
                window.setTimeout(() => {
                    window.__hf_orientation_locked__ = false;
                }, 1200);
                restoreScroll();
                resizeSpree = 0;
            }
        };
        window.addEventListener("resize", onResize, { passive: true });
        return () => {
            window.removeEventListener("orientationchange", onOrient);
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", schedulePreserveOnScroll);
        };
    }, []);

    return (
        <div className="App">
            <AppErrorBoundary>
                <AuthProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <AnalyticsTracker />
                        <PostViewerCta />
                        <AktionBanner />
                    <Routes>
                        <Route path="/" element={<HomeSwitchPage />} />
                        <Route path="/aktion" element={<AktionGate />} />
                        <Route path="/sylt" element={<SyltPage />} />
                        <Route path="/erleben" element={<ErlebenGate />} />
                        <Route path="/impressum" element={<ImpressumPage />} />
                        <Route path="/datenschutz" element={<DatenschutzPage />} />
                        <Route path="/bdv-kontakt" element={<BdvKontaktPage />} />
                        <Route path="/projekt/:slug" element={<ProjectPage />} />
                        <Route path="/admin" element={<AdminLoginPage />} />
                        <Route path="/admin/*" element={<AdminLayout />}>
                            <Route path="dashboard" element={<AdminDashboardPage />} />
                            <Route path="analytics" element={<AdminAnalyticsPage />} />
                            <Route path="analytics/sessions" element={<AdminAnalyticsSessionsPage />} />
                            <Route path="tagesbericht" element={<AdminTagesberichtPage />} />
                            <Route path="vertreter" element={<AdminVertreterListPage />} />
                            <Route path="vertreter/:id" element={<AdminVertreterDetailPage />} />
                            <Route path="vertreter/:id/anfragen" element={<AdminVertreterRequestsPage />} />
                            <Route path="anfragen" element={<AdminAnfragenListPage />} />
                            <Route path="anfragen/:id" element={<AdminAnfragenDetailPage />} />
                            <Route path="projekte" element={<AdminProjekteListPage />} />
                            <Route path="projekte/:id" element={<AdminProjektDetailPage />} />
                            <Route path="medien" element={<AdminMediaPage />} />
                            <Route path="vorher-nachher" element={<AdminVorherNachherPage />} />
                            <Route path="vorher-nachher-360" element={<AdminVorherNachher360Page />} />
                            <Route path="skybox" element={<AdminSkyboxPage />} />
                            <Route path="startseite" element={<AdminStartseitePage />} />
                            <Route path="vertrauen" element={<AdminTrustBoxPage />} />
                            <Route path="viewer-cta" element={<AdminViewerCtaPage />} />
                            <Route path="aktion" element={<AdminAktionPage />} />
                            <Route path="aktions-seite" element={<AdminAktionsSeitePage />} />
                            <Route path="rechtstexte" element={<AdminRechtstextePage />} />
                            <Route path="sylt" element={<AdminSyltPage />} />
                            <Route path="qr-kampagne" element={<AdminQRKampagnePage />} />
                        </Route>
                        {/* Vertreter-Kurzlink `hypefly.de/{nummer}`: löst die
                         *  Nummer serverseitig zum Code auf, speichert im
                         *  sessionStorage und leitet auf `/` um. Nicht-numerische
                         *  Segmente werden intern zu 404 (siehe VtCatch). */}
                        <Route path="/:vtnum" element={<VtCatch />} />
                        {/* 404-Fallback: alle unbekannten Pfade (inkl. Legacy `/poc`)
                         *  landen hier. Kein Redirect auf `/`, damit die Absenz
                         *  auch für Crawler und Playwright-Tests eindeutig ist. */}
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                </BrowserRouter>
                </AuthProvider>
            </AppErrorBoundary>
        </div>
    );
}

export default App;
